import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox } from '../combobox';
import { CommunityPageEntrySupportDto } from '@api/ChallengeClient';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';

const PAGE_SIZE = 30;

export interface CommunitySelectProps {
    value?: number;
    onChange: (communityId?: number) => void;
    label?: string;
    helperText?: string;
    size?: 'small' | 'medium';
}

export const CommunitySelect: React.FC<CommunitySelectProps> = ({
    value,
    onChange,
    label = 'Community',
    helperText,
    size = 'medium'
}) => {
    const { challengeClient } = injectTSDI(Api);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState<CommunityPageEntrySupportDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [selected, setSelected] =
        useState<CommunityPageEntrySupportDto | null>(null);

    const fetchPage = useCallback(
        async (nextPage: number, search: string) => {
            const response =
                await challengeClient.support.communitySupportControllerGetPage(
                    {
                        search: search || undefined,
                        page: nextPage,
                        size: PAGE_SIZE
                    }
                );
            const data = response.data;
            setHasMore(data.number < data.totalPages - 1);
            return data.content;
        },
        [challengeClient]
    );

    useEffect(() => {
        let active = true;
        const timerId = setTimeout(async () => {
            setLoading(true);
            try {
                const content = await fetchPage(0, searchTerm);
                if (active) {
                    setOptions(content);
                    setPage(0);
                }
            } catch (error) {
                console.error('Error fetching communities:', error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }, 300);
        return () => {
            active = false;
            clearTimeout(timerId);
        };
    }, [searchTerm, fetchPage]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) {
            return;
        }
        setLoading(true);
        try {
            const nextPage = page + 1;
            const content = await fetchPage(nextPage, searchTerm);
            setOptions((prev) => {
                const seen = new Set(prev.map((community) => community.id));
                return [
                    ...prev,
                    ...content.filter((community) => !seen.has(community.id))
                ];
            });
            setPage(nextPage);
        } catch (error) {
            console.error('Error fetching communities:', error);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, page, searchTerm, fetchPage]);

    useEffect(() => {
        if (value == null) {
            setSelected(null);
            return;
        }
        if (selected?.id === value) {
            return;
        }
        const fromOptions = options.find((c) => c.id === value);
        setSelected(
            fromOptions ??
                ({
                    id: value,
                    name: `Community #${value}`
                } as CommunityPageEntrySupportDto)
        );
    }, [value, options]);

    const mergedOptions = useMemo(() => {
        if (selected && !options.some((o) => o.id === selected.id)) {
            return [selected, ...options];
        }
        return options;
    }, [selected, options]);

    return (
        <Combobox
            size={size}
            label={label}
            helperText={helperText}
            placeholder="Select community"
            searchPlaceholder="Search communities..."
            options={mergedOptions}
            getOptionLabel={(option) =>
                option.name || `Community #${option.id}`
            }
            getOptionKey={(option) => option.id}
            value={selected}
            onChange={(newValue) => {
                setSelected(newValue);
                onChange(newValue?.id);
            }}
            onInputChange={setSearchTerm}
            onScrollEnd={() => void loadMore()}
            loading={loading}
            renderOption={(option) => (
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                        {option.name || `Community #${option.id}`}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                        #{option.id}
                    </p>
                </div>
            )}
        />
    );
};
